import { parseDateOnly, parseDateTime } from '@agroone/dates'
import {
  Intervention,
  InterventionSubTypes,
  InterventionTypes,
  Recommendation,
  RecommendationSubTypes,
  Sample,
  Scouting,
  ScoutingSubtypes,
} from '@agroone/entities'
type CropActivityType = Intervention | Scouting | Recommendation | Sample
type ActivityDate = string | null | undefined

const DATE_ONLY: RegExp = /^\d{4}-\d{2}-\d{2}$/

function getEffectiveDate(activity: CropActivityType): Date | null {
  // no date is shown on the activity card so we put them at the end of list
  if (activity instanceof Sample) {
    return null
  }
  const recommendationMapper: Record<string, (activity: Recommendation) => ActivityDate> = {
    [RecommendationSubTypes.IRRIGATION]: (a) => (a as Recommendation).irrigation?.startingDate,
    [RecommendationSubTypes.PLANTING]: (a) => (a as Recommendation).planting?.targetDate,
    [RecommendationSubTypes.OTHER_OPERATION]: (a) => (a as Recommendation).otherOperation?.targetDate,
    [RecommendationSubTypes.FOREIGN_MATERIAL]: (a) => (a as Recommendation).foreignMaterial?.targetDate,
  }
  const interventionMapper: Record<string, (activity: Intervention) => ActivityDate> = {
    [InterventionSubTypes.PESTICIDES]: (a) => a.pesticides?.completedDate,
    [InterventionSubTypes.NUTRIENTS]: (a) => a.nutrient?.targetDate,
    [InterventionSubTypes.IRRIGATION]: (a) => a.irrigation?.completedStartingDate,
    [InterventionSubTypes.MECHANICAL_WEEDING]: (a) => a.mechanicalWeeding?.completedDate,
    [InterventionSubTypes.OTHER_OPERATION]: (a) => a.otherOperation?.completedDate,
    [InterventionSubTypes.FOREIGN_MATERIAL]: (a) => a.foreignMaterial?.completedDate,
  }

  const scoutingMapper: Record<string, (activity: Scouting) => ActivityDate> = {
    [ScoutingSubtypes.FLOWERING]: (a) => (a as Scouting).flowering?.date,
    [ScoutingSubtypes.YIELD_ESTIMATE]: (a) => (a as Scouting).yieldEstimation?.forecastHarvestDate,
  }

  const resolveInterventionDate = (activity: Intervention): ActivityDate => {
    if (activity.type === InterventionTypes.FOODSAFETY) return activity.foodSafety?.completedDate
    const mapper = activity.subType === undefined ? undefined : interventionMapper[activity.subType]
    if (mapper) return mapper(activity)
    return null
  }

  const resolveRecommendationDate = (activity: Recommendation): ActivityDate => {
    const mapper =
      activity.subType === undefined || activity.subType === null ? undefined : recommendationMapper[activity.subType]
    if (mapper) return mapper(activity)
    return null
  }

  const resolveScoutingDate = (activity: Scouting): ActivityDate => {
    const mapper = activity.subType === undefined ? undefined : scoutingMapper[activity.subType]
    if (mapper) return mapper(activity)
    return null
  }

  let targetDate: ActivityDate = null

  if (activity instanceof Intervention) {
    targetDate = resolveInterventionDate(activity)
  } else if (activity instanceof Recommendation) {
    targetDate = resolveRecommendationDate(activity)
  } else if (activity instanceof Scouting) {
    targetDate = resolveScoutingDate(activity)
  }

  // Fallback to activity.date if no specific date found
  if (!targetDate && activity.date) {
    targetDate = activity.date
  }

  if (!targetDate) {
    return null
  }
  // A bare `YYYY-MM-DD` is a calendar day (local midnight), a value with a time is an instant
  return DATE_ONLY.test(targetDate) ? parseDateOnly(targetDate) : (parseDateTime(targetDate) ?? null)
}

export { getEffectiveDate }
